import {
  Component,
  HostBinding,
  Input,
  OnInit,
  Optional,
  Self,
  ChangeDetectionStrategy
} from '@angular/core';

import { tap } from 'rxjs/operators';

import { TypedFormBuilder } from 'projects/zalamo-library/src/lib/ui/form/TypedFormBuilder';
import { NgControl } from '@angular/forms';
import { AbstractValueAccessor } from 'ZalamoLibrary/src/lib/core/form/FormControl';
import { DateTime } from 'luxon';

@Component({
    selector: 'lib-date-hour-picker',
    templateUrl: './date-hour-picker.component.html',
    styleUrls: ['./date-hour-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DateHourPickerComponent
  extends AbstractValueAccessor
  implements OnInit
{
  model = this.fb.typedGroup<{
    time: string;
    date: Date | null;
  }>({
    time: ['12:00'],
    date: [null],
  });
  @Input() allowMinutes = false;
  @Input() now = new Date();
  @Input() allowPast = false;
  @Input() dateOnly = false;
  @HostBinding('class.date-only') get isDateOnly() {
    return this.dateOnly;
  }

  constructor(
    private fb: TypedFormBuilder,
    @Self() @Optional() public ngControl: NgControl,
  ) {
    super();
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.subscribeWhileAlive(
      this.valueSubject.pipe(
        // check all usages of this component, and check if it's needed
        // takeUntil(this.model.valueChanges), // lol, why? :D it stops, recieving values from model on manual set
        tap((current) => {
          const currentSeconds = Number(current);

          if (
            current === null ||
            typeof current === 'undefined' ||
            current === 0 ||
            !Number.isFinite(currentSeconds)
          ) {
            this.model.patchValue(
              {
                date: null,
                time: this.defaultTime,
              },
              { emitEvent: false },
            );
            return;
          }

          if (
            DateTime.local().toSeconds() > currentSeconds &&
            !this.allowPast
          ) {
            return;
          }

          let target = DateTime.fromSeconds(currentSeconds);

          target = this.dateOnly
            ? target.set({
                hour: 12,
                minute: 0,
              })
            : target.set({
                minute: this.allowMinutes ? target.get('minute') : 0,
              });

          this.model.patchValue(
            {
              date: target.toJSDate(),
              time: this.dateOnly
                ? this.defaultTime
                : target.toLocaleString(DateTime.TIME_24_SIMPLE),
            },
            { emitEvent: false },
          );
        }),
      ),
      this.model.valueChanges.pipe(
        tap((current) => {
          if (!current.date) {
            this.onChange(null);
            return;
          }

          let newDate = DateTime.fromJSDate(current.date);
          const [hourString = '12', minuteString = '0'] = (
            current.time || this.defaultTime
          ).split(':');
          const hour = this.dateOnly ? 12 : Number(hourString);
          const minute = this.dateOnly
            ? 0
            : this.allowMinutes
            ? Number(minuteString)
            : 0;
          newDate = newDate.set({
            hour,
            minute,
          });

          if (this.dateOnly) {
            if (current.time !== this.defaultTime) {
              this.model.patchValue(
                { time: this.defaultTime },
                { emitEvent: false },
              );
            }
          } else if (!this.allowMinutes) {
            const sanitizedTime = `${hour.toString().padStart(2, '0')}:00`;
            if (current.time !== sanitizedTime) {
              this.model.patchValue(
                { time: sanitizedTime },
                { emitEvent: false },
              );
            }
          }

          this.onChange(Math.floor(newDate.toSeconds()));
        }),
      ),
    );
  }

  private get defaultTime() {
    return '12:00';
  }
}
